import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #handleVariantClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Check if metal accordion toggle was clicked
    const toggleBtn = target?.closest?.('.variant-option__material-toggle');
    if (toggleBtn) {
      console.log('[VariantPicker] Accordion toggle clicked');
      this.toggleMaterialAccordion(event);
      return;
    }

    // Check if a material box or any of its children was clicked
    const box = target?.closest?.('.variant-option__material-box');
    if (box) {
      const radio = box.querySelector('input[type="radio"]');
      console.log('[VariantPicker] Material box clicked:', box.dataset.value, 'radio found:', radio);
      if (radio && !radio.disabled) {
        if (!radio.checked) {
          console.log('[VariantPicker] Setting radio checked & dispatching change event for:', radio.value);
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      return;
    }

    // Check if a standard button label or any of its children was clicked
    const btnLabel = target?.closest?.('.variant-option__button-label');
    if (btnLabel) {
      const radio = btnLabel.querySelector('input[type="radio"]');
      console.log('[VariantPicker] Button label clicked:', btnLabel.dataset.value, 'radio found:', radio);
      if (radio && !radio.disabled) {
        if (!radio.checked) {
          console.log('[VariantPicker] Setting radio checked & dispatching change event for:', radio.value);
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      return;
    }
  };

  /**
   * Initializes internal references for fieldsets and checked radio indices.
   */
  #initFieldsets() {
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = Array.from(this.querySelectorAll('fieldset.variant-option'));

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]') ?? []);
      this.#radios.push(radios);

      const checkedIndex = radios.findIndex((radio) => radio.checked || radio.dataset.currentChecked === 'true');
      if (checkedIndex !== -1) {
        this.#checkedIndices.push([checkedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });
    console.log('[VariantPicker] Initialized fieldsets:', fieldsets.length, 'checkedIndices:', this.#checkedIndices);
  }

  /**
   * Helper to format money value
   * @param {number|string} cents
   * @param {string} format
   * @returns {string}
   */
  formatMoney(cents, format) {
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, format);
    }
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const num = Number(cents);
    if (isNaN(num) || cents == null) return '';
    const dollars = (num / 100).toFixed(2);
    return `$${dollars}`;
  }

  /**
   * Updates all price containers on the page live in real-time.
   * @param {any} variant - The matching variant object.
   */
  updateLivePrice(variant) {
    if (!variant) return;

    if (typeof window.updateProductLivePrice === 'function') {
      window.updateProductLivePrice(variant, this);
      return;
    }

    const moneyFormat =
      (this.dataset.useCurrency === 'true' ? this.dataset.moneyWithCurrencyFormat : this.dataset.moneyFormat) ||
      window.Shopify?.money_format ||
      '${{amount}}';

    const formattedPrice = this.formatMoney(variant.price, moneyFormat);
    const hasCompare = Boolean(variant.compare_at_price && variant.compare_at_price > variant.price);
    const formattedComparePrice = hasCompare ? this.formatMoney(variant.compare_at_price, moneyFormat) : null;

    let priceTargets = document.querySelectorAll(
      'product-price [ref="priceContainer"], product-price .price-container, product-price:not(:has([ref="priceContainer"]))'
    );
    if (priceTargets.length === 0) {
      priceTargets = document.querySelectorAll('product-price, .product__price');
    }

    priceTargets.forEach((target) => {
      const container = target.matches('[ref="priceContainer"]')
        ? target
        : target.querySelector('[ref="priceContainer"]') || target;
      if (container) {
        if (hasCompare) {
          container.innerHTML = `
            <span class="price-item__group price">
              <span class="visually-hidden">Sale price </span>
              <bdi>${formattedPrice}</bdi>
            </span>
            <span class="price-item__group price-item--regular compare-at-price">
              <span class="visually-hidden">Regular price </span>
              <s class="compare-at-price-strike"><bdi>${formattedComparePrice}</bdi></s>
            </span>
          `;
        } else {
          container.innerHTML = `<span class="price"><bdi>${formattedPrice}</bdi></span>`;
        }
      }
    });

    const stickyPrices = document.querySelectorAll('.sticky-add-to-cart__price, [ref="stickyPrice"]');
    stickyPrices.forEach((el) => {
      el.innerHTML = `<bdi>${formattedPrice}</bdi>`;
    });

    const atcButtons = document.querySelectorAll(
      'button[ref="addToCartButton"], button[name="add"], .add-to-cart-button, .product-form__submit'
    );
    atcButtons.forEach((btn) => {
      if (variant.available === false) {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
      } else {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
      }

      const targetText = variant.available === false ? 'Sold Out' : 'Add to Cart';
      const textSpan = btn.querySelector('.add-to-cart-text__content');
      if (textSpan) {
        textSpan.textContent = targetText;
      } else {
        let foundTextNode = false;
        btn.childNodes.forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE && node.nodeValue.trim()) {
            node.nodeValue = ' ' + targetText + ' ';
            foundTextNode = true;
          }
        });
        if (!foundTextNode && !btn.querySelector('.add-to-cart__added')) {
          btn.textContent = targetText;
        }
      }
    });
  }

  /**
   * Finds the exact matching product variant object based on currently selected options across all fieldsets.
   * @returns {any | null}
   */
  findMatchingVariant() {
    const script = this.querySelector('script[data-product-variants]');
    if (!script || !script.textContent) return null;
    let variants = [];
    try {
      variants = JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
    if (!Array.isArray(variants) || variants.length === 0) return null;

    const selectedByPosition = {};
    const fieldsets = this.querySelectorAll('fieldset.variant-option');
    fieldsets.forEach((fieldset, idx) => {
      const checked =
        fieldset.querySelector('input[type="radio"]:checked') ||
        fieldset.querySelector('input[type="radio"][data-current-checked="true"]') ||
        fieldset.querySelector('.is-selected input[type="radio"]');
      if (checked && checked.value) {
        const pos = checked.dataset.optionPosition ? parseInt(checked.dataset.optionPosition, 10) : idx + 1;
        selectedByPosition[pos] = checked.value.trim();
      }
    });

    const selects = this.querySelectorAll('select.variant-option__select');
    selects.forEach((select, idx) => {
      if (select.value) {
        const pos = select.dataset.optionPosition ? parseInt(select.dataset.optionPosition, 10) : (fieldsets.length + idx + 1);
        selectedByPosition[pos] = select.value.trim();
      }
    });

    return (
      variants.find((v) => {
        for (const pos in selectedByPosition) {
          const idx = parseInt(pos, 10) - 1;
          const expectedVal = selectedByPosition[pos];
          if (expectedVal && v.options && v.options[idx] !== undefined) {
            if (v.options[idx].trim().toLowerCase() !== expectedVal.toLowerCase()) {
              return false;
            }
          } else if (expectedVal && v['option' + pos] !== undefined) {
            if (v['option' + pos].trim().toLowerCase() !== expectedVal.toLowerCase()) {
              return false;
            }
          }
        }
        return true;
      }) || null
    );
  }

  /**
   * Synchronizes visual active classes, checked attributes, and displayed option titles across all fieldsets.
   */
  syncSelectedVisualState() {
    const fieldsets = this.querySelectorAll('fieldset.variant-option');
    fieldsets.forEach((fieldset) => {
      const checkedInput =
        fieldset.querySelector('input[type="radio"]:checked') ||
        fieldset.querySelector('input[type="radio"][data-current-checked="true"]') ||
        fieldset.querySelector('.is-selected input[type="radio"]');
      if (checkedInput && checkedInput instanceof HTMLInputElement) {
        const val = checkedInput.value;

        const selectedValueSpan = fieldset.querySelector(
          '.variant-option__selected-value, .variant-option__selected-value strong, .variant-option__swatch-value'
        );
        if (selectedValueSpan) {
          if (selectedValueSpan.querySelector('strong')) {
            selectedValueSpan.querySelector('strong').textContent = val;
          } else {
            selectedValueSpan.textContent = val;
          }
        }

        const boxes = fieldset.querySelectorAll('.variant-option__material-box');
        boxes.forEach((b) => {
          const boxInput = b.querySelector('input[type="radio"]');
          if (boxInput === checkedInput || b.dataset.value === val || (boxInput && boxInput.value === val)) {
            b.classList.add('is-selected');
          } else {
            b.classList.remove('is-selected');
          }
        });

        const buttonLabels = fieldset.querySelectorAll('.variant-option__button-label');
        buttonLabels.forEach((label) => {
          const btnInput = label.querySelector('input[type="radio"]');
          if (btnInput === checkedInput || (btnInput && btnInput.value === val)) {
            label.classList.add('is-selected');
          } else {
            label.classList.remove('is-selected');
          }
        });
      }
    });

    const matchingVariant = this.findMatchingVariant();
    if (matchingVariant && matchingVariant.id) {
      const variantIdStr = String(matchingVariant.id);
      const formInputs = document.querySelectorAll(
        `input[ref="variantId"], input[name="id"][form="BuyButtons-ProductForm-${this.dataset.sectionId}"], form[data-type="add-to-cart-form"] input[name="id"], form[action*="/cart/add"] input[name="id"]`
      );
      formInputs.forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.value = variantIdStr;
        }
      });

      this.updateLivePrice(matchingVariant);
    }
  }

  /**
   * Toggles the metal accordion open or closed.
   * @param {Event} [event] - The click event.
   */
  toggleMaterialAccordion(event) {
    const target = /** @type {HTMLElement} */ (event?.target);
    const toggleBtn = target?.closest?.('.variant-option__material-toggle') || this.querySelector('.variant-option__material-toggle');
    if (toggleBtn && typeof window.toggleMetalAccordion === 'function') {
      window.toggleMetalAccordion(toggleBtn, event);
    } else if (toggleBtn) {
      const wrapper = toggleBtn.closest('.variant-option__material-wrapper');
      if (wrapper) {
        const isExpanded = wrapper.classList.toggle('is-expanded');
        toggleBtn.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initFieldsets();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.#handleVariantClick);
    this.#resizeObserver.observe(this);
    this.syncSelectedVisualState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleVariantClick);
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.syncSelectedVisualState();

    const matchingVariant = this.findMatchingVariant();
    const effectiveVariantId = matchingVariant ? String(matchingVariant.id) : (selectedOption.dataset.variantId || '');

    const { optionValueId = '', connectedProductUrl = '' } = selectedOption.dataset;

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    const requestUrl = this.buildRequestUrl(selectedOption);

    this.fetchUpdatedSection(requestUrl, {
      morphElementSelector,
      detail: { optionValueId, variantId: effectiveVariantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage && effectiveVariantId) {
      url.searchParams.set('variant', effectiveVariantId);
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }

      const fieldset = target.closest('fieldset');
      if (fieldset) {
        const selectedValueSpan = fieldset.querySelector('.variant-option__selected-value, .variant-option__selected-value strong, .variant-option__swatch-value');
        if (selectedValueSpan) {
          if (selectedValueSpan.querySelector('strong')) {
            selectedValueSpan.querySelector('strong').textContent = target.value;
          } else {
            selectedValueSpan.textContent = target.value;
          }
        }
        const materialBoxes = fieldset.querySelectorAll('.variant-option__material-box');
        materialBoxes.forEach((box) => box.classList.remove('is-selected'));
        const activeBox = target.closest('.variant-option__material-box');
        if (activeBox) {
          activeBox.classList.add('is-selected');
        }

        const buttonLabels = fieldset.querySelectorAll('.variant-option__button-label');
        buttonLabels.forEach((l) => l.classList.remove('is-selected'));
        const activeLabel = target.closest('.variant-option__button-label');
        if (activeLabel) {
          activeLabel.classList.add('is-selected');
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;

    const baseUrl = (productUrl || '').split('?')[0] || window.location.pathname;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    const matchingVariant = this.findMatchingVariant();

    if (matchingVariant && matchingVariant.id) {
      params.push(`variant=${matchingVariant.id}`);
    } else if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else if (selectedOption.dataset.optionValueId) {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    } else if (selectedOption.dataset.variantId) {
      params.push(`variant=${selectedOption.dataset.variantId}`);
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      params.push(`section_id=${SECTION_ID_MAP[closestSectionId]}`);
    }

    const finalUrl = `${baseUrl}?${params.join('&')}`;
    console.log('[VariantPicker] buildRequestUrl ->', finalUrl);
    return finalUrl;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;
    console.log('[VariantPicker] fetchUpdatedSection started ->', requestUrl, 'detail:', detail);
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();
    const matchingVariant = this.findMatchingVariant();
    const effectiveVariantId = variantId || (matchingVariant ? String(matchingVariant.id) : '');

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        console.log('[VariantPicker] fetchUpdatedSection response received for:', requestUrl);
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript =
          html.querySelector('variant-picker script[data-selected-variant]') ||
          html.querySelector('variant-picker script[type="application/json"]:not([data-product-variants])') ||
          html.querySelector('variant-picker script[type="application/json"]');
        const textContent = variantPickerJsonScript?.textContent;
        console.log('[VariantPicker] Parsed script JSON textContent:', textContent);

        let variantData = null;
        if (textContent) {
          try {
            const parsed = JSON.parse(textContent);
            if (Array.isArray(parsed)) {
              variantData = (effectiveVariantId && parsed.find((v) => String(v.id) === String(effectiveVariantId))) || parsed[0];
            } else {
              variantData = parsed;
            }
          } catch (e) {
            console.warn('[VariantPicker] JSON parse error:', e);
          }
        }
        if (!variantData && matchingVariant) {
          variantData = matchingVariant;
        }

        if (!variantData) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (variantData && typeof variantData === 'object' && variantData.id) {
          console.log('[VariantPicker] Resolving ProductSelectEvent with variant data:', variantData);
          this.updateLivePrice(variantData);

          const formInputs = document.querySelectorAll(
            `input[ref="variantId"], input[name="id"][form="BuyButtons-ProductForm-${this.dataset.sectionId}"], form[data-type="add-to-cart-form"] input[name="id"], form[action*="/cart/add"] input[name="id"]`
          );
          formInputs.forEach((input) => {
            if (input instanceof HTMLInputElement) {
              input.value = String(variantData.id);
            }
          });

          if (this.dataset.templateProductMatch === 'true' && !this.closest('product-card') && !this.closest('quick-add-dialog')) {
            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('variant', String(variantData.id));
            history.replaceState({}, '', currentUrl.toString());
          }

          const productViewAttr = variantPickerJsonScript
            ?.closest('[view-event-payload]')
            ?.getAttribute('view-event-payload')
            ?.trim();

          deferredEventPromise.resolve({
            variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
              sourceId: this.selectedOptionId,
              resource: variantData,
            },
          });

          return;
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('[VariantPicker] Fetch aborted by user');
        } else {
          console.error('[VariantPicker] Error fetching updated section:', error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    console.log('[VariantPicker] updateVariantPicker starting morph...');

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    const wasExpanded = this.querySelector('.variant-option__material-wrapper.is-expanded') !== null;

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    if (wasExpanded) {
      const newWrapper = this.querySelector('.variant-option__material-wrapper');
      if (newWrapper) {
        newWrapper.classList.add('is-expanded');
        const toggleBtn = newWrapper.querySelector('.variant-option__material-toggle');
        if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'true');
      }
    }

    this.#initFieldsets();
    this.syncSelectedVisualState();
    this.updateVariantPickerCss();
    console.log('[VariantPicker] updateVariantPicker completed');

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked or [data-current-checked="true"] reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked, fieldset input[data-current-checked="true"]');
    const seenNames = new Set();
    for (const input of checkedInputs) {
      if (input.dataset?.optionName && !seenNames.has(input.dataset.optionName)) {
        seenNames.add(input.dataset.optionName);
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const matchingVariant = this.findMatchingVariant();
    if (matchingVariant && matchingVariant.id) {
      return String(matchingVariant.id);
    }

    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId, variantId } = selectedOption.dataset;

    return optionValueId || variantId || selectedOption.value || undefined;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions
      .map((option) => option.dataset.optionValueId)
      .filter((id) => Boolean(id));
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
